import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Building2, IdCard, MapPin, Truck, Users, type LucideIcon } from 'lucide-react';
import { SEARCH_MIN_QUERY_LENGTH } from '@attendance/shared/searchLimits';
import type { SearchEntityType } from '@attendance/shared';
import { useAuthStore } from '../../stores/authStore.js';
import { useFeatures } from '../../stores/brandingStore.js';
import { searchApi } from '../../api/search.api.js';
import { allDestinations, navLabelOf } from './navItems.js';

/* ===========================================================================
 * THE ONE GLOBAL SEARCH — pages the reader can reach, and the records behind
 * them (people, sites, departments, positions, suppliers via `GET /api/search`).
 *
 * Shared by the app bar's search field (typed into in place) and the ⌘K
 * palette (the same search on a phone, where the bar has no room for a field),
 * so the two can never answer the same words differently.
 *
 * Three response states stay distinct: LOADING (not yet an answer), EMPTY (the
 * server answered: nothing), ERROR (the search FAILED — never shown as "no
 * results"). The pages half keeps working through all three.
 * ======================================================================== */

/** Long enough that a typed name is one request, short enough to feel instant. */
const DEBOUNCE_MS = 250;

/** Icon + heading per entity, matching the nav row each record's page sits on. */
export const ENTITY_META: Record<
  SearchEntityType,
  { icon: LucideIcon; labelKey: string; label: string }
> = {
  employee: { icon: Users, labelKey: 'command.groupEmployees', label: 'Employees' },
  worksite: { icon: MapPin, labelKey: 'command.groupWorksites', label: 'Worksites' },
  department: { icon: Building2, labelKey: 'command.groupDepartments', label: 'Departments' },
  designation: { icon: IdCard, labelKey: 'command.groupDesignations', label: 'Designations' },
  supplier: { icon: Truck, labelKey: 'command.groupSuppliers', label: 'Labour suppliers' },
};

/** One selectable line. Pages and data hits are the same shape by design, so
 *  keyboard navigation crosses the boundary without knowing it is there. */
export interface PaletteRow {
  key: string;
  to: string;
  label: string;
  sublabel: string | null;
  icon: LucideIcon;
}

export interface PaletteSection {
  id: string;
  title: string;
  rows: PaletteRow[];
}

export interface GlobalSearch {
  sections: PaletteSection[];
  /** One flat list so ↑/↓ crosses group boundaries without special cases. */
  flat: PaletteRow[];
  searching: boolean;
  dataFailed: boolean;
  typedTooShort: boolean;
  /** "Nothing matched" — only sayable when nothing is pending and nothing broke. */
  showEmpty: boolean;
  truncated: boolean;
}

/** `open`: the search is on screen. Closed, it asks the server nothing. */
export function useGlobalSearch(query: string, open: boolean): GlobalSearch {
  const role = useAuthStore((s) => s.user?.role);
  const features = useFeatures();
  const { t } = useTranslation();
  const [debounced, setDebounced] = useState('');

  // Debounce the text that feeds the network request. The pages filter below
  // stays on the RAW query so typing never feels laggy for the half of the
  // palette that needs no server.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const canSearchData = open && debounced.length >= SEARCH_MIN_QUERY_LENGTH;

  const {
    data: results,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: ({ signal }) => searchApi.global(debounced, signal),
    enabled: canSearchData,
    // A failed search must surface as an error promptly rather than sitting in
    // a retry loop that reads as "still loading".
    retry: false,
    staleTime: 30_000,
  });

  const pages = useMemo<PaletteRow[]>(() => {
    const list = allDestinations(role, features).map((d) => {
      const naming = navLabelOf(d, features);
      return { ...d, name: t(naming.labelKey, naming.label) };
    });
    const q = query.trim().toLowerCase();
    const matched = q ? list.filter((d) => d.name.toLowerCase().includes(q)) : list;
    return matched.map((d) => ({
      key: `page:${d.to}`,
      to: d.to,
      label: d.name,
      sublabel: null,
      icon: d.icon,
    }));
  }, [role, features, query, t]);

  const sections = useMemo<PaletteSection[]>(() => {
    const out: PaletteSection[] = [];
    if (pages.length > 0) {
      out.push({ id: 'pages', title: t('command.groupPages', 'Pages'), rows: pages });
    }
    // Only trust a response that belongs to the text currently in the box —
    // otherwise a slow reply for an earlier query paints stale hits under it.
    if (results && results.query === debounced) {
      results.groups.forEach((group) => {
        const meta = ENTITY_META[group.type];
        if (!meta) return;
        out.push({
          id: group.type,
          title: t(meta.labelKey, meta.label),
          rows: group.hits.map((hit) => ({
            key: `${hit.type}:${hit.id}`,
            to: hit.to,
            label: hit.label,
            sublabel: hit.sublabel,
            icon: meta.icon,
          })),
        });
      });
    }
    return out;
  }, [pages, results, debounced, t]);

  const flat = useMemo<PaletteRow[]>(() => sections.flatMap((s) => s.rows), [sections]);

  const typedTooShort = query.trim().length > 0 && query.trim().length < SEARCH_MIN_QUERY_LENGTH;
  const searching = canSearchData && isFetching;
  const dataFailed = canSearchData && isError;
  return {
    sections,
    flat,
    searching,
    dataFailed,
    typedTooShort,
    showEmpty: flat.length === 0 && !searching && !dataFailed && !typedTooShort,
    truncated: Boolean(results?.truncated && results.query === debounced && flat.length > 0),
  };
}
